import React from 'react';
import {
  Box,
  Button,
  Grid2,
  Typography,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DropzoneUploadLargFileField } from '../../shared/components/formfields/DropzoneUploadLargFileField';
import { putUpdateActivityLms } from '../../assets/nodeApi/putApi/putAdminApi';
import { postLmsActivity } from '../../assets/nodeApi/postApi/postAdminApi';
import { useToaster } from '../../shared/utils/ToasterProvider';

import theme from '../../theme';

// ─── Styles ───────────────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '14px',
    background: theme.palette.background.paper,
    '& fieldset': { borderColor: theme.palette.grey[100] },
    '&:hover fieldset': { borderColor: theme.palette.blue.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.blue.main },
  },
  '& .MuiInputLabel-root': { fontSize: '13px', color: theme.palette.secondary.light },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.blue.main },
};

const labelSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  mb: '4px',
  display: 'block',
};

const Req = () => <span style={{ color: theme.palette.error.main, marginLeft: 2 }}>*</span>;

// ─── Validation ───────────────────────────────────────────────────────────────
const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Activity name is required'),
  description: Yup.string().trim().required('Description is required'),
  age: Yup.string().trim().required('Age is required'),
  materialsRequired: Yup.string().trim().required('Materials Required is required'),
  duration: Yup.string().trim().required('Duration is required'),
});

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * EditActivityLms
 * @param {object} activityData  - the activity object from the list API
 * @param {number|string} activityId - the activity id used for the PUT endpoint
 * @param {boolean} isAddMode    - whether we are adding a new activity
 * @param {number|string} topicId - the topic id used for the POST endpoint when adding
 * @param {function} onClose    - callback to close the parent modal
 * @param {function} onSuccess  - optional callback after a successful update
 */
const EditActivityLms = ({ activityData, activityId, onClose, onSuccess, isAddMode, topicId, subjectId: propSubjectId, subject_id: propSubjectId2 }) => {
  const { showToaster } = useToaster();
  const subjectId = propSubjectId || propSubjectId2 || activityData?.subject_id || activityData?.course?.subject_id || activityData?.subjectId;

  // ── Build initial values from the incoming activity data ──────────────────
  // Keep existing video URLs as objects { id, url } to track deletions by id
  const existingVideoUrls = (activityData?.video_urls || []).map((v) =>
    typeof v === 'object' ? { id: v.id, url: v.url || '' } : { id: null, url: v || '' }
  );
  const existingFiles = (activityData?.uploaded_files || []);

  // Unified files array: existing file objects first, then a blank slot for new upload
  const initialFiles = existingFiles.length > 0
    ? existingFiles
    : [null];

  const initialValues = {
    name: activityData?.name || '',
    description: activityData?.description || '',
    age: activityData?.age || '',
    materialsRequired: activityData?.material_required || '',
    duration: activityData?.duration || '',
    // Store as { id, url } objects so we can track which existing URLs are removed
    videoUrls: existingVideoUrls.length > 0 ? existingVideoUrls : [{ id: null, url: '' }],
    // Unified: holds existing file objects (with file_type) AND new File instances
    files: initialFiles,
  };

  // ── Mutation ──────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: ({ formData, id, topicId, isAddMode }) => {
      if (isAddMode) {
        return postLmsActivity(topicId, formData);
      }
      return putUpdateActivityLms(formData, id);
    },
  });
  const queryClient = useQueryClient();

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = (values) => {
    const formData = new FormData();

    if (subjectId) {
      formData.append('subject_id', String(subjectId));
      formData.append('subjectId', String(subjectId));
    }

    if (isAddMode) {
      const activityObj = {
        name: values.name,
        description: values.description,
        age: values.age,
        material_required: values.materialsRequired,
        duration: values.duration,
        videoUrls: (values.videoUrls || []).map(v => v.url).filter(url => url.trim() !== '')
      };

      formData.append('activities', JSON.stringify([activityObj]));

      const filesToUpload = (values.files || []).filter(file => file instanceof File);
      filesToUpload.forEach((file) => {
        formData.append('files_0', file);
      });

      mutation.mutate(
        { formData, topicId, isAddMode: true },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["activitiesLms"],
            });
            showToaster('Activity added successfully!', '', 'success');
            onSuccess?.();
            onClose?.();
          },
          onError: (err) => {
            showToaster(
              err?.response?.data?.message || 'Failed to add activity',
              '',
              'error'
            );
          },
        }
      );
      return;
    }

    formData.append('name', values.name);
    formData.append('description', values.description);
    formData.append('age', values.age);
    formData.append('material_required', values.materialsRequired);
    formData.append('duration', values.duration);

    // Video URLs — split into: new (no id) → send as video_urls, removed existing → send as deleteVideoUrlIds
    const currentVideoUrls = (values.videoUrls || []);

    // IDs of existing URLs still present in the form
    const remainingVideoIds = currentVideoUrls
      .filter((v) => v.id !== null && v.url.trim() !== '')
      .map((v) => v.id);

    // Existing URLs that were removed
    const deleteVideoUrlIds = existingVideoUrls
      .filter((v) => v.id !== null && !remainingVideoIds.includes(v.id))
      .map((v) => v.id);

    // Only send brand-new URL strings (entries with no id)
    const newVideoUrls = currentVideoUrls
      .filter((v) => v.id === null && v.url.trim() !== '')
      .map((v) => v.url.trim());

    if (newVideoUrls.length > 0) {
      formData.append('video_urls', JSON.stringify(newVideoUrls));
    }

    // Determine which existing file ids were REMOVED (not present in values.files)
    const remainingExistingIds = (values.files || [])
      .filter((f) => f && typeof f === 'object' && f.file_type)
      .map((f) => f.id);

    const deleteFileUploadIds = existingFiles
      .filter((f) => !remainingExistingIds.includes(f.id))
      .map((f) => f.id);

    const allDeleteFileIds = [...deleteVideoUrlIds, ...deleteFileUploadIds];

    if (allDeleteFileIds.length > 0) {
      formData.append('deleteFileIds', JSON.stringify(allDeleteFileIds));
    }

    // New file uploads (File instances only)
    (values.files || []).forEach((file) => {
      if (file instanceof File) {
        formData.append('activity_files', file);
      }
    });
    console.log(formData)
    mutation.mutate(
      { formData, id: activityId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
      queryKey: ["activitiesLms"],
    });
          showToaster('Activity updated successfully!', '', 'success');
          onSuccess?.();
          onClose?.();
        },
        onError: (err) => {
          showToaster(
            err?.response?.data?.message || 'Failed to update activity',
            '',
            'error'
          );
        },
      }
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        fontFamily: "'Poppins', sans-serif",
        '& *': { fontFamily: "'Poppins', sans-serif !important" },
      }}
    >
      {/* Header */}
      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.palette.text.primary, mb: '4px' }}>
        {isAddMode ? 'Add Activity' : 'Edit Activity'}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: theme.palette.secondary.light, mb: '20px' }}>
        {isAddMode ? 'Add new activity details below' : 'Update the activity details below'}
      </Typography>
      <Divider sx={{ mb: '20px', borderColor: theme.palette.grey[100] }} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            <Grid2 container spacing="16px">

              {/* Activity Name */}
              <Grid2 size={12}>
                <Typography sx={labelSx}>Activity Name <Req /></Typography>
                <Field name="name">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      sx={inputSx}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid2>

              {/* Description */}
              <Grid2 size={12}>
                <Typography sx={labelSx}>Description <Req /></Typography>
                <Field name="description">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      sx={inputSx}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid2>

              {/* Materials Required */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography sx={labelSx}>Materials Required <Req /></Typography>
                <Field name="materialsRequired">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      sx={inputSx}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid2>

              {/* Duration */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography sx={labelSx}>Duration <Req /></Typography>
                <Field name="duration">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. 30 mins"
                      sx={inputSx}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid2>

              {/* Age */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography sx={labelSx}>Age <Req /></Typography>
                <Field name="age">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      sx={inputSx}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid2>

              {/* ── Video URLs ── */}
              <Grid2 size={12}>
                <Typography sx={labelSx}>Video URL</Typography>
                <FieldArray name="videoUrls">
                  {({ push: pushUrl, remove: removeUrl }) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                      {values.videoUrls && values.videoUrls.length > 0
                        ? values.videoUrls.map((urlObj, vIdx) => (
                            <Box key={vIdx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <Field name={`videoUrls[${vIdx}].url`}>
                                {({ field, meta }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    fullWidth
                                    placeholder="Enter Video URL"
                                    sx={inputSx}
                                    error={meta.touched && !!meta.error}
                                    helperText={meta.touched && meta.error}
                                    disabled={urlObj.id !== null}
                                  />
                                )}
                              </Field>
                              {values.videoUrls.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeUrl(vIdx)}
                                  sx={{ color: theme.palette.error.main, mt: 0.5 }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))
                        : null}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => pushUrl({ id: null, url: '' })}
                        sx={{
                          alignSelf: 'flex-start',
                          borderRadius: '8px',
                          borderColor: theme.palette.blue.main,
                          color: theme.palette.blue.main,
                          fontSize: '13px',
                          textTransform: 'none',
                          '&:hover': { background: theme.palette.blue.lighter },
                        }}
                      >
                        Add URL
                      </Button>
                    </Box>
                  )}
                </FieldArray>
              </Grid2>

              {/* ── Files (existing + new uploads) — same module as Activelmsadd ── */}
              <Grid2 size={12}>
                <Typography sx={labelSx}>File Upload</Typography>
                <FieldArray name="files">
                  {({ push: pushFile, remove: removeFile }) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {values.files && values.files.length > 0
                        ? values.files.map((fileItem, fIdx) => (
                            <Box key={fIdx} sx={{ position: 'relative' }}>
                              <Field
                                name={`files[${fIdx}]`}
                                component={DropzoneUploadLargFileField}
                                label="Upload File"
                                text_label={fIdx === 0 ? 'Images, PDF, DOC, DOCX (max 2 MB)' : ''}
                                // Pass existing file object so the dropzone shows its preview
                                fileDetail={
                                  typeof fileItem === 'object' && fileItem?.file_type
                                    ? fileItem
                                    : undefined
                                }
                              />
                              {values.files.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(fIdx)}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: theme.palette.error.main,
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))
                        : null}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => pushFile(null)}
                        sx={{
                          alignSelf: 'flex-start',
                          borderRadius: '8px',
                          borderColor: theme.palette.blue.main,
                          color: theme.palette.blue.main,
                          fontSize: '13px',
                          textTransform: 'none',
                          '&:hover': { background: theme.palette.blue.lighter },
                        }}
                      >
                        Add File
                      </Button>
                    </Box>
                  )}
                </FieldArray>
              </Grid2>

            </Grid2>

            <Divider sx={{ my: '24px', borderColor: theme.palette.grey[100] }} />

            {/* Footer Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderRadius: '10px',
                  borderColor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  textTransform: 'none',
                  px: '28px',
                  py: '10px',
                  '&:hover': { borderColor: theme.palette.text.primary },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={mutation.isPending}
                sx={{
                  borderRadius: '10px',
                  background: theme.palette.blue.main,
                  fontSize: '14px',
                  textTransform: 'none',
                  color: '#FFF',
                  px: '32px',
                  py: '10px',
                  boxShadow: 'none',
                  '&:hover': { background: '#1D4ED8', boxShadow: 'none' },
                }}
              >
                {mutation.isPending ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                    {isAddMode ? 'Adding...' : 'Updating...'}
                  </>
                ) : (
                  isAddMode ? 'Add Activity' : 'Update Activity'
                )}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EditActivityLms;
